import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './toast.css';

export interface ToastContent {
  title: ReactNode;
  description?: ReactNode;
}

interface ToastRecord extends ToastContent {
  id: string;
}

interface ToastDispatch {
  push: (content: ToastContent) => void;
  dismiss: (id: string) => void;
}

const DURATION_MS = 2000;

const ToastStateContext = createContext<ToastRecord[]>([]);
const ToastDispatchContext = createContext<ToastDispatch | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((content: ToastContent) => {
    idRef.current += 1;
    setToasts((prev) => [...prev, { id: `toast-${idRef.current}`, ...content }]);
  }, []);

  const dispatch = useMemo<ToastDispatch>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastDispatchContext.Provider value={dispatch}>
      <ToastStateContext.Provider value={toasts}>{children}</ToastStateContext.Provider>
    </ToastDispatchContext.Provider>
  );
}

export interface ToastApi {
  show: (content: ToastContent) => void;
}

export function useToast(): ToastApi {
  const dispatch = useContext(ToastDispatchContext);
  if (!dispatch) {
    throw new Error('useToast는 ToastProvider 내부에서 사용해야 합니다');
  }

  return useMemo(() => ({ show: (content: ToastContent) => dispatch.push(content) }), [dispatch]);
}

function ToastCheckIcon() {
  return (
    <svg className="toast__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 0C5.37262 0 0 5.37262 0 12C0 18.6277 5.37262 24 12 24C18.6277 24 24 18.6277 24 12C24 5.37262 18.6277 0 12 0ZM12 22.5236C6.21037 22.5236 1.5 17.7896 1.5 12C1.5 6.21033 6.21037 1.49995 12 1.49995C17.7896 1.49995 22.5 6.21035 22.5 12C22.5 17.7896 17.7896 22.5236 12 22.5236ZM16.7891 7.60913L9.74848 14.694L6.57785 11.5234C6.28498 11.2305 5.81023 11.2305 5.51698 11.5234C5.2241 11.8163 5.2241 12.291 5.51698 12.5839L9.2291 16.2964C9.52198 16.5889 9.99673 16.5889 10.29 16.2964C10.3237 16.2626 10.3526 16.2259 10.3789 16.1876L17.8504 8.66998C18.1429 8.3771 18.1429 7.90235 17.8504 7.60913C17.5571 7.31625 17.0824 7.31625 16.7891 7.60913Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ToastItem({ toast }: { toast: ToastRecord }) {
  const dispatch = useContext(ToastDispatchContext);
  const remainingRef = useRef(DURATION_MS);
  const startedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    function start(ms: number) {
      startedAtRef.current = Date.now();
      timerRef.current = setTimeout(() => dispatch?.dismiss(toast.id), ms);
    }

    start(remainingRef.current);

    return () => {
      if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    };
  }, [dispatch, toast.id]);

  function pause() {
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startedAtRef.current;
  }

  function resume() {
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(() => dispatch?.dismiss(toast.id), remainingRef.current);
  }

  return (
    <div
      className="toast"
      role="status"
      aria-live="polite"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: role=status 상태 알림이며, tabIndex는 포커스 자체가 목적이 아니라 자동 소멸 타이머를 키보드 포커스 시에도 일시정지하기 위한 것이다
      tabIndex={0}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <ToastCheckIcon />
      <div className="toast__desc">
        <strong>{toast.title}</strong>
        {toast.description && <p>{toast.description}</p>}
      </div>
    </div>
  );
}

export function ToastViewport() {
  const toasts = useContext(ToastStateContext);

  return (
    <div className="toast-viewport">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
